// O cliente do Supabase (~150 KB) é carregado sob demanda, depois da primeira tela: só algumas páginas usam (storage e
// consultas antigas) e elas já esperam `ready`. Antes vinha no pacote inicial de todo mundo.
import type { SupabaseClient } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type SupabaseContextValue = {
  supabase: SupabaseClient | null;
  ready: boolean;
  error: string | null;
};

type SupabaseConfig = { supabaseUrl: string; supabaseAnonKey: string };

async function getSupabaseConfig(): Promise<SupabaseConfig> {
  // Em dev: Vite injeta VITE_SUPABASE_* do .env.local — sem precisar do vercel dev
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (url && key) return { supabaseUrl: url, supabaseAnonKey: key };

  // Em produção: busca do endpoint seguro
  const r = await fetch('/api/public-config');
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<SupabaseConfig>;
}

const SupabaseContext = createContext<SupabaseContextValue>({
  supabase: null,
  ready: false,
  error: null,
});

export function SupabaseProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<SupabaseContextValue>({
    supabase: null,
    ready: false,
    error: null,
  });

  useEffect(() => {
    Promise.all([getSupabaseConfig(), import('@supabase/supabase-js')])
      .then(([{ supabaseUrl, supabaseAnonKey }, { createClient }]) => {
        const client = createClient(supabaseUrl, supabaseAnonKey);
        setValue({ supabase: client, ready: true, error: null });
      })
      .catch((err: Error) => {
        console.error('Falha ao carregar config Supabase:', err);
        setValue({ supabase: null, ready: true, error: err.message });
      });
  }, []);

  return <SupabaseContext.Provider value={value}>{children}</SupabaseContext.Provider>;
}

export function useSupabase() {
  return useContext(SupabaseContext);
}
